import { useBalance } from '@/components/future/data';
import { buysell } from '@/components/future/func';
import { OrderType, Type } from '@/components/future/type';
import { toastApp } from '@/components/toast-message/sonner';
import { HistoryTradeItem } from '@/components/trading-panel/hooks/useGetHistory';
import { percentage } from '@/components/trading-panel/hooks/useGetPosition';
import { usePairs } from '@/store/pairs';
import { useAddress } from '@/store/selector';
import { formatDisplay } from '@/utils/format-display';
import BigNumber from 'bignumber.js';

import { SvgClose } from '@/assets/ic_close';
import { IconEarn } from '@/assets/ic_earn';
import { SvgFlex, SvgReOpen } from '@/assets/svg/svg';
import SpinLoading from '@/components/loading/SpinLoading';
import SharePnl from '@/components/SharePnl/SharePnl';
import { numberFormat } from '@/utils/numberFormat';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from 'react-modal-sheet';
import { Link, useNavigate } from 'react-router';
type Props = {
    item: HistoryTradeItem;
    isOpen: boolean;
    isClose: boolean;
    onClose: () => void;
};

export default function SheetDetail({ item, isOpen, isClose, onClose }: Props) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data: usdcBalance } = useBalance();

    const [openSharePnl, setIsOpenSharePnl] = useState(false);

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);

    const pairs = usePairs((state) => state.data);

    const pairConfig = useMemo(
        () => pairs.find((p) => p.pairIndex === Number(item.pairIndex)),
        [pairs, item.pairIndex]
    );

    const handleReopenOrder = async (isBuy: boolean) => {
        if (
            new BigNumber(usdcBalance?.toString() ?? '0').lt(
                item.collateralBeforeFee
            )
        ) {
            return toastApp.error(t('Not enough balance'));
        }

        if (
            pairConfig?.maxPosition &&
            Number(item.leverage) *
                new BigNumber(item.collateralBeforeFee)
                    .dividedBy(1e18)
                    .toNumber() >
                pairConfig.maxPosition
        ) {
            return toastApp.error(
                `Max position size is ${pairConfig.maxPosition}`
            );
        }

        setIsLoading(true);
        await buysell({
            address,
            buy: isBuy,
            onSuccess: () => {
                onClose();
                navigate('/orders?tab=Position', {
                    state: { isOpened: true },
                });
            },
        })({
            pairIndex: item.pairIndex.toString(),
            leverage: item.leverage,
            amount: new BigNumber(item.collateralBeforeFee)
                .dividedBy(1e18)
                .toString(),
            orderType: OrderType.Market,
            tp: '',
            sl: '',
            type: isBuy ? Type.Long : Type.Short,
            limitPrice: '',
        });

        setIsLoading(false);
    };

    return (
        <Sheet isOpen={isOpen} onClose={onClose} detent="content-height">
            <Sheet.Backdrop className="!bg-transparent3" onTap={onClose} />

            <Sheet.Container className="!bg-transparent p-4  mb-2 flex !flex-col justify-end">
                <Sheet.Content className="h-fit !bg-transparent flex !flex-col justify-end z-50">
                    <div className="flex flex-col  !bg-[#141414] mt-auto p-6 h-fit !rounded-4xl">
                        <div className="flex justify-between items-center ">
                            <img
                                src={item.logoUrl}
                                alt={item.symbol}
                                className="w-6 h-6 rounded-full"
                            />

                            <button onClick={onClose}>
                                <SvgClose />
                            </button>
                        </div>
                        <div className="flex justify-between items-center mt-3">
                            <p className="text-white text-2xl">
                                {item.nameDisplay}
                            </p>

                            <div className="flex items-center gap-x-2 flex-1 justify-end">
                                <p
                                    className={clsx(
                                        'uppercase text-[11px] leading-4 border rounded-md ml-2 px-1',
                                        {
                                            'text-green1 border-green1':
                                                !!item.buy,
                                            '!text-red1 !border-red1':
                                                !item.buy,
                                        }
                                    )}
                                >
                                    {item.buy ? 'Long' : 'Short'}
                                </p>

                                <p className="uppercase text-[11px] leading-4 text-white border-white border rounded-md px-1.5">
                                    {item.leverage}x
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-y-5">
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Use')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {numberFormat(
                                        Number(item.initialPosToken) /
                                            Math.pow(10, item.decimals)
                                    )}{' '}
                                    WLD
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Leverage')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {item.leverage}X
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Position')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(
                                        (Number(item.initialPosToken) /
                                            Math.pow(10, 18)) *
                                            item.leverage,
                                        {
                                            decimalToShow: 2,
                                        }
                                    )}{' '}
                                    WLD
                                </p>
                            </div>

                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Entry Price')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(
                                        Number(item.openPrice) /
                                            Math.pow(10, 10),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}{' '}
                                </p>
                            </div>
                            <div className="flex justify-between items-center ">
                                <p className="text-[15px] text-[#4C4C4C]">
                                    {t('Close Price')}
                                </p>

                                <p className="text-[15px] text-white">
                                    {formatDisplay(
                                        Number(item.closePrice) /
                                            Math.pow(10, 10),
                                        {
                                            decimalToShow: item.decimalToShow,
                                        }
                                    )}
                                </p>
                            </div>

                            <div className="rounded-2xl bg-[#1C1C1C] p-4 text-white ">
                                <h2 className="font-medium text-white border-b border-dashed border-[#292929] py-4 mb-4">
                                    {t('Fees')}
                                </h2>

                                <div className="flex justify-between items-center mb-2 ">
                                    <p className="text-[15px] text-[#4C4C4C]">
                                        {t('Open fee')}
                                    </p>

                                    <p className="text-[15px] text-white">
                                        {formatDisplay(item.openFees, {
                                            decimalToShow: 2,
                                        })}{' '}
                                        WLD
                                    </p>
                                </div>
                                <div className="flex justify-between items-center mb-2 ">
                                    <p className="text-[15px] text-[#4C4C4C]">
                                        {t('Funding fee')}
                                    </p>

                                    <p className="text-[15px] text-white">
                                        {formatDisplay(item.fundingFees, {
                                            decimalToShow: 2,
                                        })}{' '}
                                        WLD
                                    </p>
                                </div>

                                <div className="flex justify-between items-center mb-2 ">
                                    <p className="text-[15px] text-[#4C4C4C]">
                                        {t('Rollover fee')}
                                    </p>

                                    <p className="text-[15px] text-white">
                                        {formatDisplay(item.rolloverFees, {
                                            decimalToShow: 2,
                                        })}{' '}
                                        WLD
                                    </p>
                                </div>

                                <div className="flex justify-between items-center mb-2 ">
                                    <p className="text-[15px] text-[#4C4C4C]">
                                        {t('Close fee')}
                                    </p>

                                    <p className="text-[15px] text-white">
                                        {formatDisplay(item.closeFees, {
                                            decimalToShow: 2,
                                        })}{' '}
                                        WLD
                                    </p>
                                </div>
                            </div>
                            <div
                                className={clsx(
                                    'rounded-2xl px-2 py-3 flex flex-col justify-center items-center mt-3 relative unrealized-box min-h-[124px]',
                                    {
                                        'bg-transparent2': item.pnlNumber >= 0,
                                        'bg-transparentRed2':
                                            item.pnlNumber < 0,
                                    }
                                )}
                            >
                                <p
                                    className={clsx(
                                        'text-left text-[15px] text-[#666] opacity-40 mb-1',
                                        {
                                            'text-green1': item.pnlNumber >= 0,
                                            'text-red1': item.pnlNumber < 0,
                                        }
                                    )}
                                >
                                    {'PNL'}: {percentage(item.pnlPercent)}
                                </p>

                                <p
                                    className={clsx(
                                        'uppercase  leading-4 text-left flex items-center text-2xl mt-2 justify-start ',
                                        {
                                            'text-green1': item.pnlNumber >= 0,
                                            'text-red1': item.pnlNumber < 0,
                                        }
                                    )}
                                >
                                    {item.pnlNumber > 0 ? '+' : ''}
                                    {formatDisplay(item.pnlNumber, {
                                        decimalToShow: 2,
                                    })}{' '}
                                    WLD
                                </p>

                                {/* Svg Animate */}
                                <svg
                                    height="100%"
                                    width="100%"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        stroke={
                                            item.pnlNumber >= 0
                                                ? 'rgba(56, 242, 74, 0.20)'
                                                : 'rgba(255, 105, 95, 0.20)'
                                        }
                                        rx="8"
                                        ry="8"
                                        className="line"
                                        height="100%"
                                        width="100%"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>

                            <div className="flex gap-x-2">
                                <button
                                    onClick={() => setIsOpenSharePnl(true)}
                                    className="flex items-center justify-center flex-1 border gap-x-2 rounded-2xl py-1 border-[#191919]"
                                >
                                    <SvgFlex />
                                    <p className="text-[#808080] text-sm">
                                        {t('Flex')}
                                    </p>
                                </button>

                                {!isClose && (
                                    <button
                                        data-action="ReopenOrder"
                                        disabled={isLoading}
                                        onClick={() =>
                                            handleReopenOrder(item.buy)
                                        }
                                        className={clsx(
                                            'flex items-center justify-center flex-1 border gap-x-2 rounded-2xl py-1 border-[#191919]',
                                            {
                                                'opacity-50': isLoading,
                                            }
                                        )}
                                    >
                                        {isLoading ? (
                                            <SpinLoading className="!w-4 !h-4" />
                                        ) : (
                                            <div className="flex items-center gap-x-1">
                                                <SvgReOpen />
                                                <p className="text-[#808080] text-sm">
                                                    {t('ReOpen')}
                                                </p>
                                            </div>
                                        )}
                                    </button>
                                )}

                                <Link
                                    to={'/earn'}
                                    onClick={() => setIsOpenSharePnl(true)}
                                    className="flex items-center justify-center flex-1 border gap-x-2 rounded-2xl py-1 border-[#191919]"
                                >
                                    <IconEarn className="text-white fill-[#808080]" />
                                    <p className="text-[#808080] text-sm">
                                        {t('Earn')}
                                    </p>
                                </Link>
                            </div>
                        </div>
                        {openSharePnl && (
                            <SharePnl
                                isOpen={openSharePnl}
                                item={item}
                                onClose={() => setIsOpenSharePnl(false)}
                            />
                        )}
                    </div>
                </Sheet.Content>
            </Sheet.Container>
        </Sheet>
    );
}
